// Stay product (sections/stay-product.liquid)
// - <stay-gallery>: one image at a time, thumbnails and arrows.
// - <stay-buy-box>: works out the order from two choices.
//   1. Who's it for: a count of dogs per size, each with its days per tin (one dog at least). The need in
//      tins a month = the sum of 30 ÷ days per tin over the dogs (one small dog: half a tin). A card is selected while its count is above zero; clicking a
//      card at zero adds a dog. No fixed most: when Shopify tracks the stock and doesn't oversell
//      (data-stock), a dog can't be added, nor a plan picked, if the tins per delivery would exceed it.
//   2. How often: the subscription plans (the delivery interval in months is read from the plan name)
//      or a one-time purchase. Tins per delivery = the need × months, rounded up (one month for one-time).
//   Each plan card shows the price a day (delivery price / (months × 30)), the struck-through full price
//   and the price per delivery. The form posts the variant, the quantity (tins per delivery) and the
//   selling plan to the cart with Dawn's sections, so the cart notification or drawer opens as usual.
//   A sticky bar slides up once the buy button has scrolled above the screen.
//   The panel's height goes to --stay-panel-height, so on desktop a buy box taller than the screen can
//   stick by its bottom edge (see the CSS).
//   Without JS the form still works: the first size and plan are checked, quantity 1.

if (!customElements.get('stay-gallery')) {
  customElements.define(
    'stay-gallery',
    class StayGallery extends HTMLElement {
      connectedCallback() {
        this.slides = [...this.querySelectorAll('.stay-product__slide')];
        this.thumbs = [...this.querySelectorAll('[data-thumb]')];
        this.index = 0;
        this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.show(Number(thumb.dataset.thumb))));
        this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.show(this.index - 1));
        this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.show(this.index + 1));
        if (this.slides.length < 2) this.querySelector('.stay-product__stage-arrows')?.setAttribute('hidden', '');
      }

      show(index) {
        const count = this.slides.length;
        this.index = (index + count) % count;
        this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === this.index));
        this.thumbs.forEach((thumb, i) => {
          if (i === this.index) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
        this.slides[this.index].querySelector('img')?.setAttribute('loading', 'eager');
      }
    }
  );
}

if (!customElements.get('stay-buy-box')) {
  customElements.define(
    'stay-buy-box',
    class StayBuyBox extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        this.section = this.closest('.stay-product');
        this.plans = JSON.parse(this.querySelector('[data-buy-plans]')?.textContent || '[]').map((plan) => ({
          ...plan,
          months: this.monthsFromName(plan.name),
        }));
        this.strings = JSON.parse(this.querySelector('[data-buy-strings]')?.textContent || '{}');
        this.basePrice = Number(this.dataset.price);
        this.stock = this.dataset.stock === undefined ? Infinity : Number(this.dataset.stock);

        this.sizeCards = [...this.querySelectorAll('[data-size-card]')];
        // Days one tin lasts one dog of each size (60 small, 30 large)
        this.sizes = this.sizeCards.map((card) => Number(card.dataset.days) || 30);
        this.sizeLabels = this.sizeCards.map((card) => card.querySelector('.stay-product__size-label')?.textContent.trim() || '');
        this.counts = this.sizes.map((_, i) => (i === 0 ? 1 : 0));
        // The first size's dog is only a starting point (so prices show at once) until the counters are used
        this.isDefault = true;
        this.planInputs = [...this.querySelectorAll('input[name^="stay-plan-"]')];

        this.planInputs.forEach((input) => input.addEventListener('change', () => this.update()));
        this.querySelectorAll('[data-size-step]').forEach((button) =>
          button.addEventListener('click', () => this.step(Number(button.dataset.sizeIndex), Number(button.dataset.sizeStep)))
        );
        // A card at zero adds a dog when clicked anywhere but its buttons
        this.sizeCards.forEach((card, index) =>
          card.addEventListener('click', (event) => {
            if (event.target.closest('button') || this.counts[index] > 0) return;
            this.step(index, 1);
          })
        );
        // Join waitlist (the section's setting): no cart form; the buttons open the client's Klaviyo form
        this.waitlist = this.dataset.waitlistForm !== undefined;
        if (!this.waitlist) this.form.addEventListener('submit', (event) => this.onSubmit(event));

        this.labelPlans();
        this.setUpBar();
        if (this.waitlist) this.setUpWaitlist();
        this.trackPanelHeight();
        this.update();
      }

      monthsFromName(name = '') {
        const text = name.toLowerCase();
        const number = (unit) => {
          const match = text.match(new RegExp(`(\\d+)\\s*${unit}`));
          return match ? Number(match[1]) : null;
        };
        const days = number('day');
        if (days) return Math.max(1, Math.round(days / 30));
        const weeks = number('week');
        if (weeks) return Math.max(1, Math.round(weeks / 4));
        const months = number('month');
        if (months) return months;
        return 1;
      }

      format(cents) {
        const format = this.dataset.moneyFormat || '£{{amount}}';
        const value = cents / 100;
        const withSeparators = (amount, decimals, thousands, decimal) => {
          const [whole, fraction] = amount.toFixed(decimals).split('.');
          return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
        };
        return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, type) => {
          switch (type) {
            case 'amount_no_decimals':
              return withSeparators(value, 0, ',', '.');
            case 'amount_with_comma_separator':
              return withSeparators(value, 2, '.', ',');
            case 'amount_no_decimals_with_comma_separator':
              return withSeparators(value, 0, '.', ',');
            default:
              return withSeparators(value, 2, ',', '.');
          }
        });
      }

      fill(template = '', values = {}) {
        return Object.entries(values).reduce(
          (text, [key, value]) => text.replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), value),
          template
        );
      }

      count(one, other, count) {
        return this.fill(count === 1 ? one : other, { count });
      }

      planLabel(months) {
        return months === 1 ? this.strings.everyMonth : this.fill(this.strings.everyMonths, { count: months });
      }

      labelPlans() {
        this.plans.forEach((plan) => {
          const card = this.planCard(plan.id);
          if (!card) return;
          card.querySelector('[data-plan-name]').textContent = this.planLabel(plan.months);
          const save = card.querySelector('[data-plan-save]');
          if (save) {
            save.textContent = plan.percent > 0 ? this.fill(this.strings.save, { percent: Math.round(plan.percent) }) : '';
            save.hidden = !(plan.percent > 0);
          }
        });
      }

      planCard(id) {
        return this.planInputs.find((input) => input.value === String(id))?.closest('.stay-product__plan');
      }

      step(index, change) {
        // Adding another size first replaces the starting dog, so someone with only a large dog needs one tap,
        // not two (client)
        if (this.isDefault && change > 0 && index !== 0) {
          this.isDefault = false;
          this.counts[0] = 0;
          this.counts[index] = 1;
          this.update();
          this.heart(index);
          return;
        }
        if (change > 0 && !this.fits(index)) return;
        const next = Math.max(0, this.counts[index] + change);
        const total = this.counts.reduce((sum, value, i) => sum + (i === index ? next : value), 0);
        if (total < 1) return;
        this.counts[index] = next;
        this.isDefault = false;
        this.update();
        if (change > 0) this.heart(index);
      }

      // A heart floats up from the dog when a dog is added (V5's; decorative, an aria-hidden layer over the cards
      // so the card's clipped corners never cut it; at most three at a time per card; none with reduced motion)
      heart(index) {
        const layer = this.querySelector('[data-buy-hearts]');
        if (!layer || !layer.animate) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const card = this.sizeCards[index];
        if (!card) return;
        this.heartCounts = this.heartCounts || {};
        if ((this.heartCounts[index] || 0) >= 3) return;

        // From the top of the dog's visible part (or the card's centre without a photo)
        const box = layer.getBoundingClientRect();
        const cardRect = card.getBoundingClientRect();
        const dog = card.querySelector('.stay-product__size-photo img, .stay-product-dog')?.getBoundingClientRect();
        const left = dog ? Math.max(dog.left, cardRect.left) : cardRect.left;
        const right = dog ? Math.min(dog.right, cardRect.right) : cardRect.right;
        const top = dog ? Math.max(dog.top, cardRect.top) : cardRect.top + cardRect.height / 2;
        const heart = document.createElement('span');
        heart.className = 'stay-product__heart';
        heart.innerHTML =
          '<svg viewBox="0 0 24 24" focusable="false"><path fill="currentColor" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 2.9 4.5 6.6 4.5c2.1 0 3.6 1.1 4.4 2.6.1.2.3.2.4.1l.6-.9c.8-1.1 2.2-1.8 3.8-1.8 3.7 0 5.7 3.9 4.2 7.3C19.5 16.4 12 21 12 21Z"/></svg>';
        heart.style.left = `${(left + right) / 2 - box.left}px`;
        heart.style.top = `${top - box.top + 8}px`;
        layer.append(heart);
        this.heartCounts[index] = (this.heartCounts[index] || 0) + 1;

        // Rises about 70px on the motion curve while it stays visible for most of the second (opacity runs
        // linearly), with a gentle sway on the inner svg; transform and opacity only
        const sway = Math.random() < 0.5 ? -1 : 1;
        const ease = getComputedStyle(document.documentElement).getPropertyValue('--stay-motion-ease').trim() || 'ease-out';
        const animation = heart.animate(
          [
            { opacity: 0, offset: 0 },
            { opacity: 1, offset: 0.12 },
            { opacity: 1, offset: 0.6 },
            { opacity: 0, offset: 1 },
          ],
          { duration: 1000, fill: 'forwards' }
        );
        heart.animate([{ transform: 'translateY(0)' }, { transform: 'translateY(-72px)' }], { duration: 1000, easing: ease, fill: 'forwards' });
        heart.querySelector('svg').animate(
          [
            { transform: 'translateX(0)' },
            { transform: `translateX(${5 * sway}px)` },
            { transform: `translateX(${-5 * sway}px)` },
            { transform: `translateX(${3 * sway}px)` },
            { transform: 'translateX(0)' },
          ],
          { duration: 1000, easing: 'ease-in-out' }
        );
        const done = () => {
          heart.remove();
          this.heartCounts[index] = Math.max(0, (this.heartCounts[index] || 1) - 1);
        };
        animation.finished.then(done, done);
      }

      // Whether one more dog of this size keeps the selected plan's delivery within the stock
      fits(index) {
        const { tinsPerMonth, plan } = this.state();
        return this.quote(plan, tinsPerMonth + 30 / this.sizes[index]).tins <= this.stock;
      }

      state() {
        // The dogs' need in tins a month, a fraction (one small dog: half a tin)
        const tinsPerMonth = this.counts.reduce((sum, count, i) => sum + (count * 30) / this.sizes[i], 0);
        const dogs = this.counts.reduce((sum, count) => sum + count, 0);
        const planInput = this.planInputs.find((input) => input.checked);
        const plan = this.plans.find((item) => String(item.id) === planInput?.value) || null;
        return { tinsPerMonth, dogs, plan };
      }

      quote(plan, tinsPerMonth) {
        const months = plan ? plan.months : 1;
        // Whole tins for the delivery, rounded up (a hair of slack for floats), one at least
        const tins = Math.max(1, Math.ceil(tinsPerMonth * months - 1e-9));
        const total = (plan ? plan.price : this.basePrice) * tins;
        return { months, tins, total, compare: this.basePrice * tins, perDay: total / (months * 30) };
      }

      update() {
        const { tinsPerMonth, dogs, plan } = this.state();
        const tinsText = (count) => this.count(this.strings.tinOne, this.strings.tinOther, count);

        this.sizeCards.forEach((card, index) => {
          card.classList.toggle('is-selected', this.counts[index] > 0);
          card.querySelector('[data-size-count]').textContent = this.counts[index];
          const badge = card.querySelector('[data-size-badge]');
          if (badge && this.counts[index] > 0) badge.textContent = `×${this.counts[index]}`;
        });
        this.querySelectorAll('[data-size-step]').forEach((button) => {
          const index = Number(button.dataset.sizeIndex);
          button.disabled = Number(button.dataset.sizeStep) > 0 ? !this.fits(index) : this.counts[index] === 0 || dogs <= 1;
        });

        // V5's wording: "Small dog · 1 tin every month", "2 × Small dog, 1 × Large dog · 12 tins every 3 months"
        const chosen = this.quote(plan, tinsPerMonth);
        const dogsText =
          dogs === 1
            ? this.sizeLabels[this.counts.findIndex((count) => count > 0)]
            : this.counts
                .map((count, i) => (count > 0 ? this.fill(this.strings.dogsOfSize, { count, size: this.sizeLabels[i] }) : ''))
                .filter(Boolean)
                .join(', ');
        const line = plan
          ? this.fill(this.strings.summaryPlan, {
              dogs: dogsText,
              tins: tinsText(chosen.tins),
              cadence: plan.months === 1 ? this.strings.cadenceMonth : this.fill(this.strings.cadenceMonths, { count: plan.months }),
            })
          : this.fill(this.strings.summaryOneTime, { dogs: dogsText, tins: tinsText(chosen.tins) });
        // Without the Who's it for? block there are no dogs to name: the line starts at the tins
        const summary = dogs ? line : line.replace(/^\s*·\s*/, '');
        const note = this.querySelector('[data-buy-size-note]');
        if (note) note.textContent = summary;

        // Every plan card, so the choice is a real comparison
        this.planInputs.forEach((input) => {
          const card = input.closest('.stay-product__plan');
          const cardPlan = this.plans.find((item) => String(item.id) === input.value) || null;
          const quote = this.quote(cardPlan, tinsPerMonth);
          // A plan whose delivery is more than the stock can't be picked (the chosen one stays as it is)
          input.disabled = !input.checked && quote.tins > this.stock;
          card.classList.toggle('is-unavailable', input.disabled);
          card.querySelector('[data-plan-tins]').textContent = this.fill(this.strings.perDeliveryTins, { tins: tinsText(quote.tins) });
          card.querySelector('[data-plan-day]').innerHTML = `<strong>${this.format(Math.round(quote.perDay))}</strong> <span>${this.strings.aDay}</span>`;
          card.querySelector('[data-plan-total]').textContent = this.fill(this.strings.perDelivery, { price: this.format(quote.total) });
          const compare = card.querySelector('[data-plan-compare]');
          if (compare) compare.textContent = cardPlan && quote.compare > quote.total ? this.format(quote.compare) : '';
        });

        if (this.bar) this.bar.querySelector('[data-buy-bar-summary]').textContent = summary;
        // Join waitlist: the buttons keep their own text, with no price, and are never disabled
        if (this.waitlist) return;

        const quote = this.quote(plan, tinsPerMonth);
        this.querySelector('[data-buy-quantity]').value = quote.tins;
        this.querySelector('[data-buy-selling-plan]').value = plan ? plan.id : '';

        const buttonText = `${this.strings.addToCart} · ${this.format(quote.total)}`;
        const label = this.querySelector('[data-buy-submit-label]');
        const submit = this.querySelector('[data-buy-submit]');
        if (label && !submit.disabled) label.textContent = buttonText;
        const unavailable = submit ? submit.disabled : false;

        if (this.bar) {
          const barButton = this.bar.querySelector('[data-buy-bar-button]');
          barButton.textContent = buttonText;
          barButton.disabled = unavailable;
        }
      }

      trackPanelHeight() {
        const panel = this.closest('.stay-product__panel');
        if (!panel) return;
        new ResizeObserver(([entry]) => {
          panel.style.setProperty('--stay-panel-height', `${Math.ceil(entry.borderBoxSize[0].blockSize)}px`);
        }).observe(panel);
      }

      setUpBar() {
        this.bar = this.section?.querySelector('[data-buy-bar]');
        const submit = this.querySelector('[data-buy-submit]');
        if (!this.bar || !submit) return;
        // Shown once Add to cart has scrolled up out of view, and hidden again while the footer is on screen,
        // so the bar never covers the footer's last lines
        let pastSubmit = false;
        let atFooter = false;
        const update = () => {
          const show = pastSubmit && !atFooter;
          this.bar.classList.toggle('is-visible', show);
          this.bar.toggleAttribute('inert', !show);
          this.bar.setAttribute('aria-hidden', String(!show));
        };
        // The observed area runs from the top of the screen far down the page, so the button only leaves it by
        // scrolling up past the top, even when a jump (End key, a fast fling) skips over the screen
        new IntersectionObserver(
          ([entry]) => {
            pastSubmit = !entry.isIntersecting;
            update();
          },
          { rootMargin: '0px 0px 100000px 0px' }
        ).observe(submit);
        const footer = document.querySelector('.shopify-section-group-footer-group');
        if (footer) {
          new IntersectionObserver(([entry]) => {
            atFooter = entry.isIntersecting;
            update();
          }).observe(footer);
        }
      }

      // The buy box's button and the sticky bar's open the Klaviyo form (Klaviyo's own queue, as its docs give it).
      // Without Klaviyo on the page (blocked, failed) they do nothing: the call only waits in the queue, and an
      // error inside Klaviyo is caught. Never a fallback to the cart.
      setUpWaitlist() {
        const formId = this.dataset.waitlistForm.trim();
        const open = () => {
          if (!formId) return;
          try {
            window._klOnsite = window._klOnsite || [];
            window._klOnsite.push(['openForm', formId]);
          } catch (error) {
            // Klaviyo's failure is not the page's: nothing to do
          }
        };
        this.querySelector('[data-buy-submit]')?.addEventListener('click', open);
        this.bar?.querySelector('[data-buy-bar-button]')?.addEventListener('click', open);
      }

      onSubmit(event) {
        event.preventDefault();
        // Without the Add to cart block, the sticky bar's button still submits (no spinner or error line then)
        const submit = this.querySelector('[data-buy-submit]');
        if (submit && (submit.getAttribute('aria-disabled') === 'true' || submit.disabled)) return;
        const error = this.querySelector('[data-buy-error]');
        if (error) error.hidden = true;

        submit?.setAttribute('aria-disabled', 'true');
        submit?.classList.add('loading');
        submit?.querySelector('.loading__spinner')?.classList.remove('hidden');

        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        const formData = new FormData(this.form);
        if (!formData.get('selling_plan')) formData.delete('selling_plan');
        if (cart) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          cart.setActiveElement(document.activeElement);
        }
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              if (error) {
                error.textContent = response.description || response.message;
                error.hidden = false;
              }
              return;
            }
            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'stay-product', productVariantId: formData.get('id'), cartData: response });
            cart.renderContents(response);
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            submit?.classList.remove('loading');
            submit?.removeAttribute('aria-disabled');
            submit?.querySelector('.loading__spinner')?.classList.add('hidden');
            if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }
    }
  );
}

// <stay-ugc>: the UGC strip under the buy box. Nothing plays on its own: each clip shows its poster and a
// play button. No native controls (no bar, sound, fullscreen or picture-in-picture): the one button, over the
// whole clip, plays it with sound and pauses it again, so a tap or click on the video does too; its label says
// which ("Play video" / "Pause video"). Playing one pauses the one playing before; a paused clip goes on
// where it stopped, an ended one starts again. A double click does nothing more. The row scrolls with
// <stay-carousel> (swipe, or drag with a mouse).
if (!customElements.get('stay-ugc')) {
  customElements.define(
    'stay-ugc',
    class StayUgc extends HTMLElement {
      connectedCallback() {
        this.videos = [...this.querySelectorAll('video')];
        // The card follows the video itself, whatever paused it (the button, another clip, the end, the system)
        this.videos.forEach((video) => {
          video.addEventListener('play', () => this.mark(video, true));
          video.addEventListener('pause', () => this.mark(video, false));
          video.addEventListener('ended', () => this.mark(video, false));
        });
        this.addEventListener('click', (event) => {
          const button = event.target.closest('[data-ugc-play]');
          if (!button) return;
          const video = button.closest('.stay-ugc__card').querySelector('video');
          if (video.paused || video.ended) this.play(video);
          else video.pause();
        });
        this.addEventListener('dblclick', (event) => {
          if (event.target.closest('.stay-ugc__media')) event.preventDefault();
        });
      }

      mark(video, playing) {
        const card = video.closest('.stay-ugc__card');
        card.classList.toggle('is-playing', playing);
        card
          .querySelector('[data-ugc-play]')
          ?.setAttribute('aria-label', playing ? this.dataset.pauseLabel : this.dataset.playLabel);
      }

      play(video) {
        this.videos.forEach((other) => {
          if (other !== video && !other.paused) other.pause();
        });
        video.muted = false;
        video.preload = 'auto';
        video.play().catch(() => this.mark(video, false));
      }
    }
  );
}
